/**
 * HomePage.jsx - CORRIGÉ
 *
 * Page d'accueil avec :
 * - Logo et description
 * - 3 sections : Cartes en vente aujourd'hui, vendues aujourd'hui, changement de prix
 * - Chaque section affiche 10 cartes
 * - Liens pour voir plus
 *
 * ✅ FIXES :
 * - Bouton "Voir toutes les cartes" → /search
 * - Bouton "Lancer" → /search?filters=...
 */

import React, { useState, useEffect } from "react";
import { Row, Col, Card, Button, Statistic, Empty, Spin, Space, Tag } from "antd";
import { ArrowRightOutlined, ShoppingOutlined, CheckOutlined, LineChartOutlined } from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import CardGrid from "../components/Cards/CardGrid";
import { searchCards } from "../api/api";
import { useTheme } from "../context/ThemeContext";

/**
 * HomePage - Page d'accueil
 */
export default function HomePage() {
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();

    const [startSellCards, setStartSellCards] = useState([]);
    const [saledCards, setSaledCards] = useState([]);
    const [priceChangeCards, setPriceChangeCards] = useState([]);

    const [totals, setTotals] = useState({
        start_sell: 0,
        saled: 0,
        price_change: 0,
    });

    const [loading, setLoading] = useState(true);

    // Récupère les cartes des 3 sections
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                // Cartes mise en vente aujourd'hui
                const startSellRes = await searchCards(
                    { start_sell_today: true },
                    1,
                    10
                );
                setStartSellCards(startSellRes.data.cards);
                setTotals((prev) => ({
                    ...prev,
                    start_sell: startSellRes.data.total || 0,
                }));

                // Cartes vendues aujourd'hui
                const saledRes = await searchCards(
                    { saled_today: true },
                    1,
                    10
                );
                setSaledCards(saledRes.data.cards);
                setTotals((prev) => ({
                    ...prev,
                    saled: saledRes.data.total || 0,
                }));

                // Cartes avec changement de prix
                const priceRes = await searchCards(
                    { price_change: true },
                    1,
                    10
                );
                setPriceChangeCards(priceRes.data.cards);
                setTotals((prev) => ({
                    ...prev,
                    price_change: priceRes.data.total || 0,
                }));
            } catch (err) {
                console.error("Erreur lors du chargement de la home", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    if (loading) {
        return (
            <div style={{ padding: "48px", textAlign: "center" }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div
            style={{
                background: isDarkMode ? "#000000" : "#fafafa",
                minHeight: "100vh",
            }}
        >
            {/* Hero Section */}
            <div
                style={{
                    background: `linear-gradient(135deg, ${isDarkMode ? "#141414" : "#ffffff"} 0%, ${isDarkMode ? "#1f1f1f" : "#f5f5f5"} 100%)`,
                    padding: "64px 24px",
                    textAlign: "center",
                    borderBottom: `1px solid ${isDarkMode ? "#434343" : "#d9d9d9"}`,
                }}
            >
                <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
                    {/* Logo grand */}
                    <img
                        src="/logo.png"
                        alt="Altered-checker logo"
                        style={{
                            width: "240px",
                            height: "240px",
                            borderRadius: "12px",
                            margin: "0 auto 24px",
                            objectFit: "contain",
                            background: isDarkMode ? "#1f1f1f" : "#f5f5f5",
                            padding: "8px"
                        }}
                    />

                    {/* Titre */}
                    <h1
                        style={{
                            fontSize: "48px",
                            fontWeight: "700",
                            margin: "0 0 16px",
                            background: "linear-gradient(135deg, #177ddc 0%, #00d4ff 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                        }}
                    >
                        Altered-checker
                    </h1>

                    {/* Description */}
                    <p
                        style={{
                            fontSize: "18px",
                            opacity: 0.7,
                            maxWidth: "600px",
                            margin: "0 auto",
                        }}
                    >
                        Suivez les variations de prix des cartes Altered en temps réel.
                        Créez des recherches sauvegardées et soyez alerté des changements.
                    </p>

                    {/* ✅ Bouton "Voir toutes les cartes" → /search */}
                    <Button
                        type="primary"
                        size="large"
                        style={{ marginTop: "24px" }}
                        onClick={() => navigate("/search")}
                    >
                        Voir toutes les cartes
                    </Button>
                </div>
            </div>

            {/* Contenu */}
            <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "48px 24px" }}>
                {/* 3 sections */}
                <Row gutter={[24, 24]} style={{ marginBottom: "48px" }}>
                    {/* Section 1 : Mise en vente */}
                    <Col xs={24} sm={12} md={8}>
                        <Card
                            style={{
                                height: "100%",
                                background: isDarkMode ? "#1f1f1f" : "#ffffff",
                                borderTop: "3px solid #52c41a",
                            }}
                            hoverable
                        >
                            <Space direction="vertical" style={{ width: "100%" }}>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "12px",
                                    }}
                                >
                                    <ShoppingOutlined
                                        style={{ fontSize: "24px", color: "#52c41a" }}
                                    />
                                    <h3 style={{ margin: 0 }}>Mise en vente</h3>
                                </div>

                                <Statistic
                                    value={totals.start_sell}
                                    suffix="cartes"
                                    valueStyle={{ color: "#52c41a" }}
                                />

                                <p style={{ margin: 0, opacity: 0.6, fontSize: "14px" }}>
                                    Aujourd'hui
                                </p>

                                <Button
                                    type="primary"
                                    block
                                    icon={<ArrowRightOutlined />}
                                    onClick={() => navigate("/search?start_sell_today=true")}
                                >
                                    Voir les cartes
                                </Button>
                            </Space>
                        </Card>
                    </Col>

                    {/* Section 2 : Vendues */}
                    <Col xs={24} sm={12} md={8}>
                        <Card
                            style={{
                                height: "100%",
                                background: isDarkMode ? "#1f1f1f" : "#ffffff",
                                borderTop: "3px solid #faad14",
                            }}
                            hoverable
                        >
                            <Space direction="vertical" style={{ width: "100%" }}>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "12px",
                                    }}
                                >
                                    <CheckOutlined
                                        style={{ fontSize: "24px", color: "#faad14" }}
                                    />
                                    <h3 style={{ margin: 0 }}>Vendues</h3>
                                </div>

                                <Statistic
                                    value={totals.saled}
                                    suffix="cartes"
                                    valueStyle={{ color: "#faad14" }}
                                />

                                <p style={{ margin: 0, opacity: 0.6, fontSize: "14px" }}>
                                    Aujourd'hui
                                </p>

                                <Button
                                    type="primary"
                                    block
                                    icon={<ArrowRightOutlined />}
                                    onClick={() => navigate("/search?saled_today=true")}>
                                    Voir l'historique
                                </Button>
                            </Space>
                        </Card>
                    </Col>

                    {/* Section 3 : Changement de prix */}
                    <Col xs={24} sm={12} md={8}>
                        <Card
                            style={{
                                height: "100%",
                                background: isDarkMode ? "#1f1f1f" : "#ffffff",
                                borderTop: "3px solid #1890ff",
                            }}
                            hoverable
                        >
                            <Space direction="vertical" style={{ width: "100%" }}>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "12px",
                                    }}
                                >
                                    <LineChartOutlined
                                        style={{ fontSize: "24px", color: "#1890ff" }}
                                    />
                                    <h3 style={{ margin: 0 }}>Changements</h3>
                                </div>

                                <Statistic
                                    value={totals.price_change}
                                    suffix="cartes"
                                    valueStyle={{ color: "#1890ff" }}
                                />

                                <p style={{ margin: 0, opacity: 0.6, fontSize: "14px" }}>
                                    Prix modifiés
                                </p>

                                <Button
                                    type="primary"
                                    block
                                    icon={<ArrowRightOutlined />}
                                    onClick={() => navigate("/search?price_change=true")}>
                                    Voir les cartes
                                </Button>
                            </Space>
                        </Card>
                    </Col>
                </Row>

                {/* Cartes mise en vente */}
                <Card
                    title={
                        <Space>
                            <ShoppingOutlined />
                            <span>Cartes mises en vente aujourd'hui</span>
                            <Tag color="green">{totals.start_sell} cartes</Tag>
                        </Space>
                    }
                    style={{ background: isDarkMode ? "#1f1f1f" : "#ffffff", marginBottom: "32px" }}
                >
                    {totals.start_sell > 0 ? (
                        <>
                            <CardGrid cards={startSellCards} />
                            <div style={{ textAlign: "center", marginTop: "16px" }}>
                                <Button
                                    type="primary"
                                    size="large"
                                    onClick={() => navigate("/search?start_sell_today=true")}
                                >
                                    Voir tous les {totals.start_sell} cartes mises en vente
                                </Button>
                            </div>
                        </>
                    ) : (
                        <Empty description="Aucune carte" />
                    )}
                </Card>

                {/* Cartes vendues */}
                <Card
                    title={
                        <Space>
                            <CheckOutlined />
                            <span>Cartes vendues aujourd'hui</span>
                            <Tag color="orange">{totals.saled} cartes</Tag>
                        </Space>
                    }
                    style={{ background: isDarkMode ? "#1f1f1f" : "#ffffff", marginBottom: "32px" }}
                >
                    {totals.saled > 0 ? (
                        <>
                            <CardGrid cards={saledCards} />
                            <div style={{ textAlign: "center", marginTop: "16px" }}>
                                <Button
                                    type="primary"
                                    size="large"
                                    onClick={() => navigate("/search?saled_today=true")}
                                >
                                    Voir tous les {totals.saled} vendus aujourd'hui
                                </Button>
                            </div>
                        </>

                    ) : (
                        <Empty description="Aucune carte" />
                    )}

                </Card>

                {/* Cartes avec changement de prix */}
                <Card
                    title={
                        <Space>
                            <LineChartOutlined />
                            <span>Cartes avec changement de prix</span>
                            <Tag color="blue">{totals.price_change} cartes</Tag>
                        </Space>
                    }
                    style={{ background: isDarkMode ? "#1f1f1f" : "#ffffff" }}
                >
                    {totals.price_change > 0 ? (
                        <>
                            <CardGrid cards={priceChangeCards} />
                            <div style={{ textAlign: "center", marginTop: "16px" }}>
                                <Button
                                    type="primary"
                                    size="large"
                                    onClick={() => navigate("/search?price_change=true")}
                                >
                                    Voir tous les {totals.price_change} changement de prix
                                </Button>
                            </div>
                        </>
                    ) : (
                        <Empty description="Aucune carte" />
                    )}
                </Card>
            </div>
        </div>
    );
}
